import { useMutation } from '@tanstack/react-query';
import useGetMarketingData from './useGetMarketingData';
import { invalidateEmailTemplates } from '../../services/marketing';
import {
  createAutomation,
  invalidateAutomations,
} from '../../services/automations';
import { toCreateAutomationRequest } from '../../helpers/marketing/automations';
import { AutomationFormValues } from '../../types/marketing/automations';

interface Options {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onMutate?: () => void;
  onSettled?: () => void;
}

export async function submitAutomation(
  marketingBusinessId: string,
  values: AutomationFormValues,
) {
  const request = toCreateAutomationRequest(marketingBusinessId, values);
  if (!request) throw new Error('Please finish the trigger and content');
  await createAutomation(request);
}

function useCreateAutomation({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { data: marketingData } = useGetMarketingData();

  return useMutation({
    mutationFn: async (values: AutomationFormValues) => {
      const marketingBusinessId = marketingData?.id;
      if (!marketingBusinessId) throw new Error('Marketing is not set up yet');
      await submitAutomation(marketingBusinessId, values);
    },
    onSuccess: async () => {
      await invalidateAutomations();
      await invalidateEmailTemplates();
      onSuccess?.();
    },
    onError: async error => {
      await invalidateEmailTemplates();
      onError?.(error.message);
    },
    onMutate,
    onSettled,
  });
}

export default useCreateAutomation;
